import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { batch, useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { useIsMobile } from '../../hooks/useIsMobile'
import { useIsTablet } from '../../hooks/useIsTablet'
import { getDetailsById } from '../../redux/actions/DesignDetailsAction'
import { getFollowList } from '../../redux/actions/FollowingActions'
import { PriceTagClear } from '../../redux/actions/PriceTagActions'
import { GetAllSavedProducts } from '../../redux/actions/SaveProductAction'
import { setHideFloatingButtons } from '../../redux/designerReducers/locationStateReducer'
import { CaptionModal } from '../CaptionModal'
import Detail from '../DesignerCommon/Detail'
import DiscoverButtonCTA from '../DiscoverButton/DiscoverButtonCTA'
import { LoadingIndicator2 } from '../Loading'
import { AIMetadata } from './AIMetadata'
import * as styles from './details.module.css'
import { Main } from './Main'
import { MoreDesignsByUser } from './MoreDesignsByUser'
import { Products } from './Products'

// Styled Components
const AIMetadataContainer = styled.div`
  margin: 20px auto 18px;
  padding-left: 11px;
  padding-right: 11px;

  @media (min-width: 800px) {
    margin-top: 50px;
    max-width: 1197px;
    padding-right: 0;
    padding: 0;
  }
`

const DetailContainer = styled.div`
  zoom: ${props => props.zoom || 1};
  z-index: 1;
`

const Details = ({ initialPost, userDesigns, preloadedItems }) => {
  const dispatch = useDispatch()
  const { query } = useRouter()
  const { data: designDetailsData, objectId } = useSelector(state => state.designdetails)
  const id = query?.designId?.split('-').pop()

  useEffect(() => {
    if (id) {
      batch(() => {
        // Only fetch if we don't already have data for this ID
        if (!designDetailsData || objectId !== id) {
          dispatch(getDetailsById(id))
        }
        dispatch(PriceTagClear())
        dispatch(GetAllSavedProducts())
        dispatch(getFollowList())
      })
    }
  }, [dispatch, id, designDetailsData, objectId])

  return (
    <>
      <DetailsContainer initialPost={initialPost} userDesigns={userDesigns} preloadedItems={preloadedItems} />
      <DiscoverButtonCTA higher={true} />
    </>
  )
}

const DetailsContainer = ({ initialPost, userDesigns, preloadedItems }) => {
  const { query } = useRouter()
  const id = query.designId?.split('-').pop()
  const { data: mainData, loading, likes: active, numLikes } = useSelector(state => state.designdetails)
  const { allSavedProducts } = useSelector(state => state.saveProduct) // all saved products with objectids
  const [zoom, setZoom] = useState(1)
  const [show, setShow] = useState(true)
  const isMobile = useIsMobile()
  const isTablet = useIsTablet()
  const dispatch = useDispatch()
  const [showCaptionModal, setShowCaptionModal] = useState(false)
  const prerenderedData = initialPost && initialPost.objectId ? initialPost : null
  const hasFetchedData = mainData && mainData.objectId
  const detailData = hasFetchedData ? mainData : prerenderedData
  const detailActive = typeof active === 'boolean' ? active : false
  const detailNumLikes = hasFetchedData && typeof numLikes === 'number' ? numLikes : prerenderedData?.numLikes || 0
  const isContentReady = Boolean(detailData && detailData.objectId)
  const isLoading = loading && !isContentReady

  function showDiv(e) {
    if (!isMobile && !isTablet) return

    if (e.target.id === 'currentItem' || e.target.id === 'currentArrow' || e.target.parentNode.id === 'currentArrow') {
      if (show) {
        dispatch(setHideFloatingButtons(true))

        setShow(false)
        document.getElementById('products').style.height = 'auto'
        document.getElementById('showall').style.display = 'flex'
        document.getElementById('currentItemIcon').classList.add('currentItemIcon')
      } else {
        dispatch(setHideFloatingButtons(false))

        setShow(true)
        document.getElementById('products').style.height = '85px'
        document.getElementById('showall').style.display = 'none'
        document.getElementById('currentItemIcon').classList.remove('currentItemIcon')
      }
    }
  }

  useEffect(() => {
    function updateSize() {
      if (!isMobile && !isTablet) return

      if (window.innerWidth >= 1034 && window.innerWidth <= 1376) {
        document.getElementById('products').style.height = '800px'
        document.getElementById('showall').style.display = 'block'
        document.getElementById('currentItemIcon').classList.add('currentItemIcon')
        setZoom(window.innerWidth / 1366)
      } else if (window.innerWidth >= 1376) {
        document.getElementById('products').style.height = '800px'
        document.getElementById('showall').style.display = 'block'
        document.getElementById('currentItemIcon').classList.add('currentItemIcon')
        setZoom(1)
      } else {
        document.getElementById('products').style.height = '85px'
        document.getElementById('showall').style.display = 'none'
        document.getElementById('currentItemIcon').classList.remove('currentItemIcon')
        setZoom(1)
      }
    }
    window.addEventListener('resize', updateSize)
    return () => window.removeEventListener('resize', updateSize)
  }, [isMobile, isTablet])

  return (
    <>
      <LoadingIndicator2 loading={isLoading} />
      {isContentReady && (
        <>
          <div className={styles.backContainer}>
            <Detail title="" />
          </div>
          <AIMetadataContainer>
            <AIMetadata aiTitle={detailData?.aiTitle} aiDescription={detailData?.aiDescription} />
          </AIMetadataContainer>
          <DetailContainer className={`${styles.detailContainer} ${styles.noModal}`} zoom={zoom} onClick={showDiv}>
            <Main
              id={id}
              active={detailActive}
              data={detailData}
              numLikes={detailNumLikes}
              showCaptionModal={() => {
                setShowCaptionModal(true)
              }}
            />
            <Products id={id} data={detailData} allSavedProducts={allSavedProducts} preloadedItems={preloadedItems} />
          </DetailContainer>
        </>
      )}
      <MoreDesignsByUser userName={detailData?.user?.uniqueDisplayName} userDesigns={userDesigns} />
      {!!detailData?.objectId && showCaptionModal && <CaptionModal data={detailData} handleClose={() => setShowCaptionModal(false)} />}
    </>
  )
}

export default Details
